import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { Dialog, hideDialog } from '../../base/dialog';
import Icon from '../../base/icons/components/Icon';
import { IconCrown, IconMicrophone, IconUserGroup } from '../../base/icons/svg';
import { approveRequest } from '../../visitors/actions';
// The dialogs in this fork render on a white surface while theme.palette assumes
// the dark Jitsi theme, so the colors here are explicit — palette-derived ones
// came out as solid blue blocks.
const useStyles = makeStyles()(() => {
    return {
        description: {
            color: '#5E6D7A',
            fontSize: 13,
            lineHeight: '18px',
            margin: '0 0 16px'
        },
        options: {
            display: 'grid',
            gap: 10
        },
        option: {
            '&:focus-visible': {
                borderColor: '#4B6790',
                boxShadow: '0 0 0 2px rgba(75, 103, 144, 0.25)'
            },
            '&:hover': {
                backgroundColor: '#F4F8FD',
                borderColor: '#C7D5E9'
            },
            alignItems: 'center',
            backgroundColor: '#fff',
            border: '1px solid #E4EBF5',
            borderRadius: 10,
            boxShadow: '0 1px 2px rgba(19, 21, 25, 0.04)',
            color: '#131519',
            cursor: 'pointer',
            display: 'grid',
            fontFamily: 'inherit',
            gap: 12,
            gridTemplateColumns: '36px minmax(0, 1fr)',
            minHeight: 64,
            padding: '12px',
            textAlign: 'left',
            transition: 'background-color .15s ease, border-color .15s ease',
            width: '100%'
        },
        icon: {
            alignItems: 'center',
            backgroundColor: '#EEF3FA',
            borderRadius: 8,
            display: 'flex',
            height: 36,
            justifyContent: 'center',
            width: 36
        },
        text: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0
        },
        title: {
            color: '#131519',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '18px'
        },
        detail: {
            color: '#5E6D7A',
            fontSize: 12,
            lineHeight: '16px',
            marginTop: 2
        }
    };
});
/**
 * Displays the available main-room roles for an approved viewer.
 *
 * @param {IProps} props - Component props.
 * @returns {JSX.Element}
 */
const VisitorPromotionDialog = ({ request }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const admitAs = useCallback((role) => {
        dispatch(approveRequest(request, role));
        dispatch(hideDialog(VisitorPromotionDialog));
    }, [dispatch, request]);
    const options = [
        {
            description: 'participantsPane.actions.admitAsParticipantDescription',
            icon: IconUserGroup,
            label: 'participantsPane.actions.admitAsParticipant',
            role: 'participant'
        },
        {
            description: 'participantsPane.actions.admitAsPanelistDescription',
            icon: IconMicrophone,
            label: 'participantsPane.actions.admitAsPanelist',
            role: 'panelist'
        },
        {
            description: 'participantsPane.actions.admitAsModeratorDescription',
            icon: IconCrown,
            label: 'participantsPane.actions.admitAsModerator',
            role: 'moderator'
        }
    ];
    return (React.createElement(Dialog, { disableFooter: true, onSubmit: () => true, titleKey: 'participantsPane.actions.admitAs', width: 'small' },
        React.createElement("p", { className: classes.description }, t('participantsPane.actions.admitDialogDescription', { name: request.nick })),
        React.createElement("div", { className: classes.options }, options.map(option => (React.createElement("button", { className: classes.option, key: option.role, onClick: () => admitAs(option.role), type: 'button' },
            React.createElement("span", { className: classes.icon },
                React.createElement(Icon, { color: '#4B6790', size: 20, src: option.icon })),
            React.createElement("span", { className: classes.text },
                React.createElement("span", { className: classes.title }, t(option.label)),
                React.createElement("span", { className: classes.detail }, t(option.description)))))))));
};
export default VisitorPromotionDialog;
